import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';

const SLOT_LABELS = { morning: 'Morning', midday: 'Midday', evening: 'Evening', custom: 'Custom' };
const SLOT_ICONS = { morning: '🌅', midday: '☀️', evening: '🌙', custom: '🕐' };
const HAY_TYPES = ['Alfalfa', 'Bermuda', 'Orchard Grass', 'Timothy', 'Teff', 'Custom'];
const FEED_METHODS = [
  { value: 'haynet', label: 'In a haynet' },
  { value: 'bucket', label: 'Feed in feed bucket' },
  { value: 'ground', label: 'Feed on ground' },
  { value: 'rack', label: 'Feed in hay rack' },
  { value: 'other', label: 'Other' },
];
const SUPPLEMENT_UNITS = [
  { value: 'scoop', label: 'Scoop' },
  { value: 'teaspoon', label: 'Teaspoon' },
  { value: 'tablespoon', label: 'Tablespoon' },
  { value: 'gram', label: 'Gram (g)' },
  { value: 'mg', label: 'Milligram (mg)' },
  { value: 'oz', label: 'Ounce (oz)' },
  { value: 'ml', label: 'mL / cc' },
  { value: 'capsule', label: 'Capsule' },
  { value: 'tablet', label: 'Tablet' },
  { value: 'packet', label: 'Packet' },
  { value: 'pump', label: 'Pump' },
  { value: 'drop', label: 'Drop' },
  { value: 'serving', label: 'Serving' },
  { value: 'custom', label: 'Custom' },
];

const methodLabel = (v) => FEED_METHODS.find(m => m.value === v)?.label || v;
const supplementUnitLabel = (v) => SUPPLEMENT_UNITS.find(u => u.value === v)?.label || v;
const supplementDetail = (s) => [s.dose && `${s.dose} ${supplementUnitLabel(s.unit || 'scoop')}`, s.soaked ? 'soaked' : null, s.notes].filter(Boolean).join(' · ');
const itemIcon = (type) => type === 'hay' ? '🟩' : '🌾';
const itemTypeLabel = (type) => type === 'hay' ? 'Hay' : 'Feed';

// Add/edit a feeding time
const FeedingTimeModal = ({ horseId, existing, onClose, onSaved }) => {
  const [slot, setSlot] = useState(existing?.slot || 'morning');
  const [label, setLabel] = useState(existing?.label || '');
  const [time, setTime] = useState(existing?.time || '');
  const [alertEnabled, setAlertEnabled] = useState(existing?.alert_enabled || false);
  const [note, setNote] = useState(existing?.note || '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { slot, label, time, alert_enabled: alertEnabled, note };
      if (existing) await api.put(`/feeding/${existing.id}`, payload);
      else await api.post(`/feeding/horse/${horseId}`, payload);
      toast.success(existing ? 'Updated' : 'Feeding time added');
      onSaved();
    } catch { toast.error('Failed'); } finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 999, display: 'flex', alignItems: 'flex-end' }}>
      <div style={{ background: 'white', borderRadius: '20px 20px 0 0', padding: 20, width: '100%', maxWidth: 480, margin: '0 auto', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 14 }}>
          <h2>{existing ? 'Edit feeding time' : 'Add feeding time'}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer' }}>×</button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div className="form-group">
            <label className="form-label">When</label>
            <select className="form-input" value={slot} onChange={e => setSlot(e.target.value)}>
              <option value="morning">Morning</option>
              <option value="midday">Midday</option>
              <option value="evening">Evening</option>
              <option value="custom">Custom</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Time</label>
            <input className="form-input" type="time" value={time} onChange={e => setTime(e.target.value)} />
          </div>
        </div>
        {slot === 'custom' && (
          <div className="form-group">
            <label className="form-label">Label</label>
            <input className="form-input" value={label} onChange={e => setLabel(e.target.value)} placeholder="e.g. Pre-class" />
          </div>
        )}
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer', fontSize: 14, fontWeight: 600, padding: '6px 0' }}>
          <input type="checkbox" checked={alertEnabled} onChange={e => setAlertEnabled(e.target.checked)} style={{ accentColor: 'var(--cr-primary)' }} />
          Remind me at this time
        </label>
        <div className="form-group">
          <label className="form-label">Note (optional)</label>
          <input className="form-input" value={note} onChange={e => setNote(e.target.value)} placeholder="e.g. soak the grain first" />
        </div>
        <button className="btn btn-primary btn-full" onClick={save} disabled={saving}>{saving ? 'Saving…' : (existing ? 'Save' : 'Add feeding time')}</button>
      </div>
    </div>
  );
};

// Add a supplement directly into a feeding time
const AddSupplementModal = ({ horseId, feedingTimeId, onClose, onSaved }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [picked, setPicked] = useState(null);
  const [dose, setDose] = useState('');
  const [unit, setUnit] = useState('scoop');
  const [soaked, setSoaked] = useState(false);
  const [notes, setNotes] = useState('');
  const [requestPromotion, setRequestPromotion] = useState(false);
  const [saving, setSaving] = useState(false);
  const debounceRef = useRef(null);

  const runSearch = useCallback((q) => {
    setSearching(true);
    api.get(`/catalog/search?category=supplement&q=${encodeURIComponent(q)}`)
      .then(r => setResults(r.data.results || [])).catch(() => setResults([])).finally(() => setSearching(false));
  }, []);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(query), 250);
    return () => clearTimeout(debounceRef.current);
  }, [query, runSearch]);

  const noMatch = query.trim() && !searching && results.length === 0;

  const save = async () => {
    const name = picked ? picked.name : query.trim();
    if (!name) return toast.error('Choose or type a supplement');
    setSaving(true);
    try {
      if (!picked) await api.post('/catalog/private', { name, category: 'supplement', request_promotion: requestPromotion });
      await api.post(`/catalog/horse/${horseId}`, { name, subcategory: picked?.subcategory, dose, unit, soaked, notes, feeding_time_id: feedingTimeId });
      toast.success('Added');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); } finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 999, display: 'flex', alignItems: 'flex-end' }}>
      <div style={{ background: 'white', borderRadius: '20px 20px 0 0', padding: 20, width: '100%', maxWidth: 480, margin: '0 auto', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 14 }}>
          <h2>Add supplement</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer' }}>×</button>
        </div>
        {!picked && (
          <>
            <div className="form-group">
              <label className="form-label">Search supplements</label>
              <input className="form-input" value={query} onChange={e => setQuery(e.target.value)} placeholder="Start typing, e.g. Cosequin" autoComplete="off" autoFocus />
            </div>
            {searching && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Searching…</div>}
            {results.length > 0 && (
              <div style={{ border: '1px solid var(--border-color)', borderRadius: 8, maxHeight: 240, overflowY: 'auto', marginBottom: 12 }}>
                {results.map(r => (
                  <div key={r.id} onClick={() => setPicked(r)} style={{ padding: '10px 12px', borderBottom: '1px solid var(--border-color)', cursor: 'pointer' }}>
                    <div style={{ fontSize: 14, fontWeight: 500 }}>{r.name}</div>
                    {r.subcategory && <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{r.subcategory}</div>}
                  </div>
                ))}
              </div>
            )}
            {noMatch && (
              <div style={{ background: 'var(--bg-secondary)', borderRadius: 8, padding: 12, marginBottom: 12 }}>
                <div style={{ fontSize: 13, marginBottom: 8 }}>No match for "<strong>{query.trim()}</strong>". Add it as your own item.</div>
                <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, cursor: 'pointer' }}>
                  <input type="checkbox" checked={requestPromotion} onChange={e => setRequestPromotion(e.target.checked)} style={{ accentColor: 'var(--cr-primary)' }} />
                  Also request it be added to the master catalog
                </label>
              </div>
            )}
          </>
        )}
        {(picked || noMatch) && (
          <>
            <div style={{ background: 'var(--cr-primary-light)', borderRadius: 8, padding: 12, marginBottom: 12 }}>
              <div style={{ fontSize: 14, fontWeight: 600 }}>{picked ? picked.name : query.trim()}</div>
              <button onClick={() => setPicked(null)} style={{ background: 'none', border: 'none', color: 'var(--cr-primary)', cursor: 'pointer', fontSize: 12, marginTop: 4, padding: 0 }}>Change</button>
            </div>
            <div className="form-group">
              <label className="form-label">Dose / amount</label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <input className="form-input" value={dose} onChange={e => setDose(e.target.value)} placeholder="e.g. 1" inputMode="decimal" />
                <select className="form-input" value={unit} onChange={e => setUnit(e.target.value)}>
                  {SUPPLEMENT_UNITS.map(u => <option key={u.value} value={u.value}>{u.label}</option>)}
                </select>
              </div>
            </div>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer', fontSize: 13, padding: '4px 0 10px' }}>
              <input type="checkbox" checked={soaked} onChange={e => setSoaked(e.target.checked)} style={{ accentColor: 'var(--cr-primary)' }} />
              Soaked
            </label>
            <div className="form-group"><label className="form-label">Notes / directions (optional)</label><input className="form-input" value={notes} onChange={e => setNotes(e.target.value)} placeholder="e.g. mix with wet feed" /></div>
            <button className="btn btn-primary btn-full" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Add'}</button>
          </>
        )}
      </div>
    </div>
  );
};

const FeedingItemModal = ({ feedingTimeId, type, existing, onClose, onSaved }) => {
  const itemType = existing?.type || type || 'feed';
  const [name, setName] = useState(existing?.name || (itemType === 'hay' ? 'Alfalfa' : ''));
  const [customHay, setCustomHay] = useState(existing?.type === 'hay' && !HAY_TYPES.includes(existing?.name) ? existing.name : '');
  const [amount, setAmount] = useState(existing?.amount || '');
  const [unit, setUnit] = useState(existing?.unit || (itemType === 'hay' ? 'flake' : 'lb'));
  const [soaked, setSoaked] = useState(!!existing?.soaked);
  const [feedMethod, setFeedMethod] = useState(existing?.feed_method || 'haynet');
  const [notes, setNotes] = useState(existing?.notes || '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const finalName = itemType === 'hay' && name === 'Custom' ? customHay.trim() : name.trim();
    if (!finalName) return toast.error('Name required');
    setSaving(true);
    try {
      const payload = { type: itemType, name: finalName, amount, unit, soaked, feed_method: itemType === 'hay' ? feedMethod : null, notes };
      if (existing) await api.put(`/feeding/items/${existing.id}`, payload);
      else await api.post(`/feeding/${feedingTimeId}/items`, payload);
      toast.success(existing ? 'Updated' : 'Added');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); } finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 999, display: 'flex', alignItems: 'flex-end' }}>
      <div style={{ background: 'white', borderRadius: '20px 20px 0 0', padding: 20, width: '100%', maxWidth: 480, margin: '0 auto', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 14 }}>
          <h2>{existing ? 'Edit' : 'Add'} {itemTypeLabel(itemType).toLowerCase()}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer' }}>×</button>
        </div>
        {itemType === 'hay' ? (
          <>
            <div className="form-group">
              <label className="form-label">Hay type</label>
              <select className="form-input" value={HAY_TYPES.includes(name) ? name : 'Custom'} onChange={e => setName(e.target.value)}>
                {HAY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            {(name === 'Custom' || !HAY_TYPES.includes(name)) && (
              <div className="form-group">
                <label className="form-label">Custom hay name</label>
                <input className="form-input" value={customHay} onChange={e => setCustomHay(e.target.value)} placeholder="e.g. Coastal mix" />
              </div>
            )}
          </>
        ) : (
          <div className="form-group">
            <label className="form-label">Feed name</label>
            <input className="form-input" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Strategy, Senior feed" autoFocus />
          </div>
        )}
        <div className="form-group">
          <label className="form-label">Amount</label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <input className="form-input" value={amount} onChange={e => setAmount(e.target.value)} placeholder="e.g. 2" inputMode="decimal" />
            <select className="form-input" value={unit} onChange={e => setUnit(e.target.value)}>
              {itemType === 'hay' ? <><option value="flake">Flakes</option><option value="weight">Weight</option></> : <><option value="lb">Pounds (lb)</option><option value="scoop">Scoops</option></>}
            </select>
          </div>
        </div>
        {itemType === 'hay' && (
          <div className="form-group">
            <label className="form-label">How is it fed?</label>
            <select className="form-input" value={feedMethod} onChange={e => setFeedMethod(e.target.value)}>
              {FEED_METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>
        )}
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer', fontSize: 13, padding: '4px 0 10px' }}>
          <input type="checkbox" checked={soaked} onChange={e => setSoaked(e.target.checked)} style={{ accentColor: 'var(--cr-primary)' }} />
          Soaked
        </label>
        <div className="form-group"><label className="form-label">Notes (optional)</label><input className="form-input" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional notes" /></div>
        <button className="btn btn-primary btn-full" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
      </div>
    </div>
  );
};


const EditSupplementModal = ({ supplement, onClose, onSaved }) => {
  const [dose, setDose] = useState(supplement?.dose || '');
  const [unit, setUnit] = useState(supplement?.unit || 'scoop');
  const [soaked, setSoaked] = useState(!!supplement?.soaked);
  const [notes, setNotes] = useState(supplement?.notes || '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/catalog/supplement/${supplement.id}`, { dose, unit, soaked, notes, frequency: null });
      toast.success('Updated');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); } finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 999, display: 'flex', alignItems: 'flex-end' }}>
      <div style={{ background: 'white', borderRadius: '20px 20px 0 0', padding: 20, width: '100%', maxWidth: 480, margin: '0 auto', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 14 }}>
          <h2>Edit supplement</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer' }}>×</button>
        </div>
        <div style={{ background: 'var(--cr-primary-light)', borderRadius: 8, padding: 12, marginBottom: 12 }}>
          <div style={{ fontSize: 14, fontWeight: 600 }}>{supplement.name}</div>
          <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>This only changes this horse's copy, not the saved stack.</div>
        </div>
        <div className="form-group">
          <label className="form-label">Dose / amount</label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <input className="form-input" value={dose} onChange={e => setDose(e.target.value)} placeholder="e.g. 1" inputMode="decimal" />
            <select className="form-input" value={unit} onChange={e => setUnit(e.target.value)}>
              {SUPPLEMENT_UNITS.map(u => <option key={u.value} value={u.value}>{u.label}</option>)}
            </select>
          </div>
        </div>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer', fontSize: 13, padding: '4px 0 10px' }}>
          <input type="checkbox" checked={soaked} onChange={e => setSoaked(e.target.checked)} style={{ accentColor: 'var(--cr-primary)' }} />
          Soaked
        </label>
        <div className="form-group"><label className="form-label">Notes / directions (optional)</label><input className="form-input" value={notes} onChange={e => setNotes(e.target.value)} placeholder="e.g. mix with wet feed" /></div>
        <button className="btn btn-primary btn-full" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save supplement'}</button>
      </div>
    </div>
  );
};

const FeedSupplementsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [horse, setHorse] = useState(null);
  const [schedule, setSchedule] = useState({ feeding_times: [], unscheduled: [] });
  const [loading, setLoading] = useState(true);
  const [addingTime, setAddingTime] = useState(false);
  const [editingTime, setEditingTime] = useState(null);
  const [addSuppTo, setAddSuppTo] = useState(null);
  const [addItemTo, setAddItemTo] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [editSupp, setEditSupp] = useState(null);

  const load = async () => {
    try {
      const [h, s] = await Promise.all([api.get(`/horses/${id}`), api.get(`/feeding/horse/${id}`)]);
      setHorse(h.data); setSchedule(s.data);
    } catch { toast.error('Failed to load'); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [id]);

  const removeSupp = async (sid) => {
    if (!window.confirm('Remove this supplement?')) return;
    try { await api.delete(`/catalog/supplement/${sid}`); load(); } catch { toast.error('Failed'); }
  };
  const removeItem = async (itemId) => {
    if (!window.confirm('Remove this feed/hay item?')) return;
    try { await api.delete(`/feeding/items/${itemId}`); load(); } catch { toast.error('Failed'); }
  };
  const removeTime = async (ftId) => {
    if (!window.confirm('Remove this feeding time? Supplements in it stay on the horse, just unscheduled. Feed and hay items in it will be removed.')) return;
    try { await api.delete(`/feeding/${ftId}`); load(); } catch { toast.error('Failed'); }
  };

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-secondary)', paddingBottom: 40 }}>
      <div className="top-bar">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button onClick={() => navigate(`/horses/${id}`)} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', fontSize: 20, padding: '4px 8px 4px 0' }}>‹</button>
          <div style={{ flex: 1 }}>
            <h1 style={{ color: 'white', fontSize: 17 }}>Feed, hay & supplements</h1>
            <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: 12 }}>{horse?.name}</p>
          </div>
          <button onClick={() => setAddingTime(true)} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', cursor: 'pointer', fontSize: 13, borderRadius: 8, padding: '6px 14px', fontWeight: 500 }}>+ Time</button>
        </div>
      </div>

      <div style={{ padding: 12 }}>
        {schedule.feeding_times.length === 0 && schedule.unscheduled.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">🥣</div>
            <h3>No feeding times yet</h3>
            <p>Add a feeding time, then add feed, hay, or supplements. You can also apply stacks from Horse Tools.</p>
            <button className="btn btn-primary" onClick={() => setAddingTime(true)}>+ Add feeding time</button>
          </div>
        ) : (
          <>
            {schedule.feeding_times.map(ft => (
              <div key={ft.id} className="card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 18 }}>{SLOT_ICONS[ft.slot] || '🕐'}</span>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{ft.label || SLOT_LABELS[ft.slot]}</div>
                      {ft.time && <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{ft.time}</div>}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    {ft.alert_enabled && <span className="badge badge-green" style={{ fontSize: 10 }}>Alert</span>}
                    <button onClick={() => setEditingTime(ft)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--cr-primary)' }}>Edit</button>
                  </div>
                </div>
                {ft.note && <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontStyle: 'italic', marginBottom: 8 }}>📝 {ft.note}</div>}

                {(ft.items || []).map(item => (
                  <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderTop: '1px solid var(--border-color)' }}>
                    <span style={{ fontSize: 15 }}>{itemIcon(item.type)}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>{item.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{[item.amount && `${item.amount} ${item.unit || (item.type === 'hay' ? 'flake' : 'lb')}`, item.type === 'hay' && item.feed_method ? methodLabel(item.feed_method) : null, item.soaked ? 'soaked' : null, item.notes].filter(Boolean).join(' · ')}</div>
                    </div>
                    <button onClick={() => setEditItem(item)} style={{ background: 'none', border: 'none', color: 'var(--cr-primary)', cursor: 'pointer', fontSize: 12 }}>Edit</button>
                    <button onClick={() => removeItem(item.id)} style={{ background: 'none', border: 'none', color: 'var(--cr-red)', cursor: 'pointer', fontSize: 12 }}>Remove</button>
                  </div>
                ))}

                {(ft.supplements || []).map(s => (
                  <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderTop: '1px solid var(--border-color)' }}>
                    <span style={{ fontSize: 15 }}>💊</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>{s.name}</div>
                      {supplementDetail(s) && <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{supplementDetail(s)}</div>}
                    </div>
                    <button onClick={() => setEditSupp(s)} style={{ background: 'none', border: 'none', color: 'var(--cr-primary)', cursor: 'pointer', fontSize: 12 }}>Edit</button>
                    <button onClick={() => removeSupp(s.id)} style={{ background: 'none', border: 'none', color: 'var(--cr-red)', cursor: 'pointer', fontSize: 12 }}>Remove</button>
                  </div>
                ))}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginTop: 8 }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => setAddItemTo({ feedingTimeId: ft.id, type: 'feed' })}>+ Feed</button>
                  <button className="btn btn-secondary btn-sm" onClick={() => setAddItemTo({ feedingTimeId: ft.id, type: 'hay' })}>+ Hay</button>
                  <button className="btn btn-secondary btn-sm" onClick={() => setAddSuppTo(ft.id)}>+ Supplement</button>
                </div>
                <button className="btn btn-danger btn-sm" style={{ marginTop: 8, width: '100%' }} onClick={() => removeTime(ft.id)}>Delete time</button>
              </div>
            ))}

            {schedule.unscheduled.length > 0 && (
              <>
                <div className="section-label">Not assigned to a feeding time</div>
                {schedule.unscheduled.map(s => (
                  <div key={s.id} className="card" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 15 }}>💊</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>{s.name}</div>
                      {supplementDetail(s) && <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{supplementDetail(s)}</div>}
                    </div>
                    <button onClick={() => setEditSupp(s)} style={{ background: 'none', border: 'none', color: 'var(--cr-primary)', cursor: 'pointer', fontSize: 12 }}>Edit</button>
                    <button onClick={() => removeSupp(s.id)} style={{ background: 'none', border: 'none', color: 'var(--cr-red)', cursor: 'pointer', fontSize: 12 }}>Remove</button>
                  </div>
                ))}
              </>
            )}
          </>
        )}
      </div>

      {addingTime && <FeedingTimeModal horseId={id} onClose={() => setAddingTime(false)} onSaved={() => { setAddingTime(false); load(); }} />}
      {editingTime && <FeedingTimeModal horseId={id} existing={editingTime} onClose={() => setEditingTime(null)} onSaved={() => { setEditingTime(null); load(); }} />}
      {addSuppTo && <AddSupplementModal horseId={id} feedingTimeId={addSuppTo} onClose={() => setAddSuppTo(null)} onSaved={() => { setAddSuppTo(null); load(); }} />}
      {addItemTo && <FeedingItemModal feedingTimeId={addItemTo.feedingTimeId} type={addItemTo.type} onClose={() => setAddItemTo(null)} onSaved={() => { setAddItemTo(null); load(); }} />}
      {editItem && <FeedingItemModal existing={editItem} onClose={() => setEditItem(null)} onSaved={() => { setEditItem(null); load(); }} />}
      {editSupp && <EditSupplementModal supplement={editSupp} onClose={() => setEditSupp(null)} onSaved={() => { setEditSupp(null); load(); }} />}
    </div>
  );
};

export default FeedSupplementsPage;
